import { Button } from "@aitisal/ui/retail-storefront/button";
import { useNavigate } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Loader2,
} from "lucide-react";
import { useRef } from "react";
import ResponsiveImage from "../ResponsiveImage";
import { getTitle } from "../../helper/helper";
import { useActiveMenu } from "../../hooks/useActiveMenu";

export default function CategoryCircular() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const { sections, menu } = useActiveMenu();
  const isLoading = !menu;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-20">
        <Loader2 className="animate-spin text-gray-400 w-8 h-8" />
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <section className="w-full py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <ImageOff className="w-10 h-10 text-gray-300" />
            <p className="text-gray-500 font-medium">No categories yet</p>
            <p className="text-sm text-gray-400">
              Check back soon — the store hasn't added any categories.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative z-10 w-full pt-10 bg-white">
      <div className="relative max-w-7xl mx-auto px-4">
        {/* Left Navigation Button */}
        <Button
          onClick={() => scroll("left")}
          variant="outline"
          size="icon"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 rounded-full w-7 h-7 md:w-10 md:h-10 bg-white border-2 border-gray-300 hover:bg-gray-50 shadow-md"
          aria-label="Scroll Left"
        >
          <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-700" />
        </Button>

        {/* Scrollable Container */}
        <div
          ref={scrollContainerRef}
          className="overflow-x-auto no-scrollbar scroll-smooth"
        >
          <div className="flex items-center gap-6 px-12">
            {sections.map((section: any) => {
              const sectionImage = section?.image;
              const title = getTitle(
                section?.name || { translations: { en: "Category" } },
              );

              const handleCategoryClick = () => {
                navigate({
                  to: "/c/shop",
                  search: { category: title, search: undefined },
                });
              };

              return (
                <div
                  key={section?.sectionId}
                  className="flex flex-col items-center gap-3 cursor-pointer group flex-shrink-0 transition-transform hover:scale-105"
                  onClick={handleCategoryClick}
                >
                  {/* Circular Image Container */}
                  <div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 group-hover:border-gray-400 transition-colors shadow-md flex items-center justify-center">
                    {sectionImage ? (
                      <ResponsiveImage
                        imageObject={sectionImage}
                        sizes="(min-width: 1024px) 128px, (min-width: 640px) 96px, 80px"
                        alt={title}
                        className="absolute inset-0 h-full w-full"
                        style={{ height: "100%" }}
                      />
                    ) : (
                      <ImageOff className="w-8 h-8 text-gray-300" />
                    )}
                  </div>

                  {/* Category Name */}
                  <span className="text-sm md:text-base font-medium text-gray-900 text-center max-w-[120px] lg:max-w-[140px] leading-tight">
                    {title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Navigation Button */}
        <Button
          onClick={() => scroll("right")}
          variant="outline"
          size="icon"
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 rounded-full w-7 h-7 md:w-10 md:h-10 bg-white border-2 border-gray-300 hover:bg-gray-50 shadow-md"
          aria-label="Scroll Right"
        >
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-gray-700" />
        </Button>
      </div>
    </section>
  );
}
